import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PanelCard,
  StatusPill,
  TextField,
  ToggleField,
} from './panelUi'
import { getSetting, type UserSettings } from './useUserSettings'
import { useWriteSettings } from './useWriteSettings'
import { useToast } from '../ui'

type ValidationState = 'idle' | 'loading' | 'valid' | 'invalid'

/**
 * PWA Groq settings — mirrors GroqSettingsPanel.tsx in the desktop.
 * Validates the key by calling the public Groq models endpoint with a
 * 300ms debounce. Also exposes a master AI-search toggle that gates the
 * `useAiSearch` hook (consumers can check `settings.aiSearchEnabled`).
 */
export function GroqSettingsPanel({
  settings,
}: {
  settings: UserSettings | null
}) {
  const { updateSetting } = useWriteSettings()
  const { showSuccess, showError } = useToast()
  const [showKey, setShowKey] = useState(false)
  const [keyDraft, setKeyDraft] = useState('')
  const [validation, setValidation] = useState<ValidationState>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    setKeyDraft(settings?.groqApiKey ?? '')
  }, [settings?.groqApiKey])

  // Debounced validation against Groq's /openai/v1/models endpoint. This is
  // the same shape OpenAI uses and Groq exposes it publicly for auth probing.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const key = keyDraft.trim()
    if (!key) {
      setValidation('idle')
      setValidationError(null)
      return
    }
    setValidation('loading')
    setValidationError(null)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch('https://api.groq.com/openai/v1/models', {
          headers: { Authorization: `Bearer ${key}` },
        })
        if (!res.ok) {
          setValidation('invalid')
          setValidationError(`Groq rejected the key (HTTP ${res.status})`)
          return
        }
        const data = await res.json()
        if (Array.isArray(data?.data)) {
          setValidation('valid')
          setValidationError(null)
        } else {
          setValidation('invalid')
          setValidationError('Unexpected response from Groq')
        }
      } catch (err) {
        setValidation('invalid')
        setValidationError((err as Error).message)
      }
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [keyDraft])

  const savedKey = settings?.groqApiKey ?? ''
  const dirty = keyDraft !== savedKey
  const canSave =
    dirty && (keyDraft.trim() === '' || validation === 'valid')

  const statusPill = useMemo(() => {
    if (validation === 'loading')
      return <StatusPill kind="loading" label="Checking…" />
    if (validation === 'valid') return <StatusPill kind="ok" label="Valid" />
    if (validation === 'invalid')
      return <StatusPill kind="error" label="Invalid" />
    if (savedKey) return <StatusPill kind="ok" label="Connected" />
    return <StatusPill kind="idle" label="Not set" />
  }, [validation, savedKey])

  const aiEnabled = getSetting(settings, 'aiSearchEnabled')

  return (
    <PanelCard
      title="Groq AI"
      description='Powers natural-language search ("90s heist like Heat but lighter").'
      status={statusPill}
    >
      <TextField
        label="Groq API key"
        type={showKey ? 'text' : 'password'}
        value={keyDraft}
        placeholder="gsk_..."
        onChange={setKeyDraft}
        rightSlot={
          <>
            <button
              type="button"
              onClick={() => setShowKey((s) => !s)}
              className="rounded-md border border-border-primary bg-bg-tertiary px-2 text-[11px] text-text-secondary hover:bg-bg-primary"
              title={showKey ? 'Hide key' : 'Show key'}
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
            <button
              type="button"
              disabled={!canSave}
              onClick={async () => {
                try {
                  const trimmed = keyDraft.trim()
                  await updateSetting(
                    'groqApiKey',
                    trimmed ? trimmed : null
                  )
                  if (trimmed) {
                    showSuccess('Groq connected')
                  } else {
                    showSuccess('Groq key cleared')
                  }
                } catch (err) {
                  console.error('[GroqSettingsPanel] save failed', err)
                  showError('Failed to save Groq key')
                }
              }}
              className="rounded-md bg-accent-primary px-2.5 text-[11px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Save
            </button>
          </>
        }
        description={
          <>
            Get a free key at{' '}
            <a
              href="https://console.groq.com/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-primary underline"
            >
              console.groq.com
            </a>
            . {validationError && (
              <span className="text-red-400">{validationError}</span>
            )}
          </>
        }
      />

      <ToggleField
        label="Enable AI search"
        description="Use Groq to interpret natural-language search queries"
        checked={Boolean(aiEnabled)}
        onChange={(v) => updateSetting('aiSearchEnabled', v)}
      />
    </PanelCard>
  )
}
